import { useFetcher } from '@remix-run/react';
import { Building2, Check, ChevronDown, Loader2, Search } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { MerchantContextState } from '~/providers/merchant-context/merchant-context';

interface SwitchResponse {
  ok: boolean;
  error?: string;
  requestId?: string | null;
}

export function MerchantSwitcher({
  state,
  mobile = false,
}: {
  state: MerchantContextState;
  mobile?: boolean;
}) {
  const fetcher = useFetcher<SwitchResponse>();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const rootRef = useRef<HTMLDivElement>(null);
  const merchants = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return state.merchants;
    return state.merchants.filter(
      (item) =>
        item.name.toLowerCase().includes(term) ||
        item.id.includes(term) ||
        item.marketMerchantId?.toLowerCase().includes(term),
    );
  }, [search, state.merchants]);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node))
        setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  useEffect(() => {
    if (fetcher.state === 'idle' && fetcher.data?.ok)
      window.location.assign('/');
  }, [fetcher.data, fetcher.state]);

  const select = (merchantId: string) => {
    if (merchantId === state.activeMerchant?.id) return setOpen(false);
    fetcher.submit(
      { merchantId },
      { method: 'post', action: '/api/merchant-context' },
    );
  };

  return (
    <div
      ref={rootRef}
      className={`relative ${
        mobile ? 'w-full mb-4' : 'hidden md:block max-w-[230px]'
      }`}
    >
      <button
        type="button"
        onClick={() => setOpen((value) => !value)}
        disabled={fetcher.state !== 'idle'}
        className={`flex items-center gap-2 border rounded-xl bg-white hover:border-primary/50 disabled:opacity-60 ${
          mobile ? 'w-full px-3 py-3' : 'px-3 py-2 max-w-[230px]'
        }`}
        aria-label="Select purchasing merchant"
      >
        {fetcher.state !== 'idle' ? (
          <Loader2 className="w-4 h-4 animate-spin" />
        ) : (
          <Building2 className="w-4 h-4 text-primary flex-shrink-0" />
        )}
        <span className="min-w-0 flex-1 text-left">
          <span className="block text-[10px] uppercase text-gray-400 leading-none mb-1">
            Purchasing as
          </span>
          <span className="block truncate text-xs font-semibold text-gray-800">
            {state.activeMerchant?.name || 'Select merchant'}
          </span>
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </button>

      {open && (
        <div
          className={`absolute z-[120] mt-2 bg-white border border-gray-100 rounded-xl shadow-2xl overflow-hidden ${
            mobile ? 'left-0 right-0' : 'right-0 w-80'
          }`}
        >
          <div className="p-3 border-b border-gray-100">
            <div className="flex items-center gap-2 rounded-lg bg-gray-50 px-3">
              <Search className="w-4 h-4 text-gray-400" />
              <input
                autoFocus
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                placeholder="Search name or merchant ID"
                className="w-full border-0 bg-transparent py-2 text-sm focus:ring-0"
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto p-2">
            {merchants.length ? (
              merchants.map((merchant) => (
                <button
                  key={merchant.id}
                  type="button"
                  onClick={() => select(merchant.id)}
                  className="w-full flex items-center gap-3 rounded-lg px-3 py-2.5 text-left hover:bg-gray-50"
                >
                  <Building2 className="w-4 h-4 text-gray-400" />
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm font-medium truncate">
                      {merchant.name}
                    </span>
                    <span className="block text-xs text-gray-400">
                      ID {merchant.id}
                      {merchant.marketMerchantId
                        ? ` · Market ${merchant.marketMerchantId}`
                        : ''}
                    </span>
                  </span>
                  {state.activeMerchant?.id === merchant.id && (
                    <Check className="w-4 h-4 text-green-600" />
                  )}
                </button>
              ))
            ) : (
              <p className="px-3 py-6 text-center text-sm text-gray-500">
                No eligible merchants found.
              </p>
            )}
          </div>
        </div>
      )}
      {fetcher.data && !fetcher.data.ok && (
        <p className="mt-1 text-xs text-red-600">
          {fetcher.data.error}
          {fetcher.data.requestId
            ? ` Reference: ${fetcher.data.requestId}`
            : ''}
        </p>
      )}
    </div>
  );
}
